// frontend/src/features/ogdb2/components/ImportExportPanel.jsx
import React, { useMemo, useState, useEffect } from "react";
import api from "../../../apiClient";
import { OGDB2 } from "../ogdb2Api";
import BAIDImportPreviewModal from "./BAIDImportPreviewModal";
import ColumnMapperModal from "./ColumnMapperModal";

function toAbs(urlish) {
  if (!urlish) return "";
  try {
    const u = new URL(urlish);
    return u.toString();
  } catch {
    const base = (api?.defaults?.baseURL || window.location.origin).replace(/\/+$/, "");
    return new URL(urlish, `${base}/`).toString();
  }
}

function downloadFile(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

function BusyOverlay({ open, title = "Working…", subtitle = "" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 w-[min(560px,92vw)]">
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        <div className="text-xs text-gray-600 mt-2">
          {subtitle || "Please don’t close the app while this finishes."}
        </div>
        <div className="mt-4 h-2 w-full bg-gray-100 rounded overflow-hidden">
          <div className="h-2 bg-gray-900 rounded animate-pulse w-2/3" />
        </div>
        <div className="mt-3 text-[11px] text-gray-500">
          If this takes longer than expected, it may be writing a lot of facts (columns) to the database.
        </div>
      </div>
    </div>
  );
}

function mappingKey({ client, file_type, firm }) {
  const c = String(client || "").trim() || "_";
  const ft = String(file_type || "").trim() || "_";
  const f = String(firm || "").trim() || "_";
  return `ogdb2_column_map_v1::${c}::${ft}::${f}`;
}

function loadMappingTemplate(k) {
  try {
    const raw = localStorage.getItem(k);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveMappingTemplate(k, arr) {
  try {
    localStorage.setItem(k, JSON.stringify(Array.isArray(arr) ? arr : []));
  } catch {}
}

const APPRAISER_FIRMS = [
  { value: "capitol", label: "Capitol Appraisal Group" },
  { value: "pritchard_abbott", label: "Pritchard & Abbott" },
  { value: "wardlaw", label: "Wardlaw Appraisal Group" },
  { value: "thomas_y_pickett", label: "Thomas Y Pickett" },
];

export default function ImportExportPanel({ onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [lastImportFileId, setLastImportFileId] = useState(null);

  // BAID Import UI state
  const [baidClient, setBaidClient] = useState("");
  const [baidFileType, setBaidFileType] = useState("appraisal_roll");
  const [baidTaxYear, setBaidTaxYear] = useState("");
  const [baidFirm, setBaidFirm] = useState("capitol");
  const [baidNotes, setBaidNotes] = useState("");
  const [baidFile, setBaidFile] = useState(null);
  // v4 BAID: required for lease_summary + appraisal_roll imports
  const [baidCounty, setBaidCounty] = useState("");

  // ✅ Crosswalk file (Master Insurance List) for Client LOS imports
  const [crosswalkFile, setCrosswalkFile] = useState(null);

  // attach-only default for non-seed imports
  const [createMissingBaids, setCreateMissingBaids] = useState(false);

  // Preview control (rows)
  const [previewLimit, setPreviewLimit] = useState(500);

  const [baidUnits, setBaidUnits] = useState([]);

  // BAID detail modal (existing)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailErr, setDetailErr] = useState("");
  const [detailUnit, setDetailUnit] = useState(null);
  const [detailRows, setDetailRows] = useState([]);

  // Preview modal
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewErr, setPreviewErr] = useState("");
  const [previewData, setPreviewData] = useState(null);

  // ✅ Column mapper modal
  const [mapperOpen, setMapperOpen] = useState(false);
  const [mappingDraft, setMappingDraft] = useState([]); // array of {raw, metric_code, scenario}

  const currentMapKey = useMemo(() => {
    return mappingKey({ client: baidClient, file_type: baidFileType, firm: baidFirm });
  }, [baidClient, baidFileType, baidFirm]);

  // When the import identity changes, auto-load the template
  useEffect(() => {
    const c = (baidClient || "").trim();
    if (!c) return;
    setMappingDraft(loadMappingTemplate(currentMapKey));
  }, [currentMapKey, baidClient]);

  // If user flips to a file type where firm is controlled, ensure a valid default.
  useEffect(() => {
    const ft = (baidFileType || "").trim().toLowerCase();
    const firmControlled = ft === "lease_summary" || ft === "appraisal_roll";
    if (firmControlled) {
      const v = (baidFirm || "").trim();
      const ok = APPRAISER_FIRMS.some((x) => x.value === v);
      if (!ok) setBaidFirm("capitol");
    }
  }, [baidFileType]); // eslint-disable-line react-hooks/exhaustive-deps

  async function uploadCsv(file) {
    if (!file) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      const res = await OGDB2.importCsv(file);
      const s = res?.data;

      const extraErrors =
        Array.isArray(s?.errors) && s.errors.length ? ` (${s.errors.length} warnings)` : "";

      setMsg(
        `Imported CSV. Wells created: ${s.created_wells}, wells updated: ${s.updated_wells}, ` +
          `metrics created: ${s.created_metrics}, metrics updated: ${s.updated_metrics}, ` +
          `skipped rows: ${s.skipped_rows}.${extraErrors}`
      );

      onRefresh?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Import failed");
    } finally {
      setBusy(false);
    }
  }

  async function refreshBAIDUnits(client) {
    const c = (client || "").trim();
    if (!c) return;
    try {
      const res = await OGDB2.listBAIDUnits(c, 200);
      setBaidUnits(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.log("Failed to list BAID units:", e);
    }
  }

  function downloadLastDebugXlsx() {
    if (!lastImportFileId) return;
    const url = toAbs(`/ogdb2/imports/${lastImportFileId}/debug.xlsx`);
    downloadFile(url);
  }

  function effectiveAllowCreate() {
    return baidFileType === "lease_summary" ? true : !!createMissingBaids;
  }

  function validateBAIDInputs() {
    const client = (baidClient || "").trim();
    if (!client) return "BAID Import: client is required.";
    if (!baidFile) return "BAID Import: please choose a file.";

    const ft = String(baidFileType || "").trim().toLowerCase();
    const name = String(baidFile?.name || "").toLowerCase();

    const isGasSearch = ft === "gassearch" || ft === "gas_search";

    // v4 BAID: county is required for lease_summary + appraisal_roll so the
    // same RRC in different counties produces separate BAIDs.
    if (ft === "lease_summary" || ft === "appraisal_roll") {
      if (!(baidCounty || "").trim()) {
        return "County is required for lease_summary and appraisal_roll imports.";
      }
    }

    if (isGasSearch) {
      const ok = name.endsWith(".csv") || name.endsWith(".xlsx") || name.endsWith(".xls");
      if (!ok) return "GasSearch import: please choose a .csv or .xlsx file.";
      return null;
    }

    // Crosswalk is optional for Client LOS — name matching is primary

    const okExcel = name.endsWith(".xlsx") || name.endsWith(".xls");
    if (!okExcel) return "BAID Import: please choose an Excel file (.xlsx/.xls).";
    return null;
  }

  async function runPreview() {
    const v = validateBAIDInputs();
    if (v) {
      setErr(v);
      return;
    }

    const client = (baidClient || "").trim();

    const lim = Number(previewLimit || 0);
    const safeLim = Number.isFinite(lim) ? Math.max(1, Math.min(5000, lim)) : 500;

    setPreviewOpen(true);
    setPreviewBusy(true);
    setPreviewErr("");
    setPreviewData(null);

    try {
      const res = await OGDB2.previewImportExcel({
        file: baidFile,
        client,
        file_type: baidFileType,
        tax_year: baidTaxYear ? baidTaxYear : undefined,
        appraiser_firm: (isGasSearch || isClientLOS) ? undefined : (baidFirm || "").trim() || undefined,
        notes: (baidNotes || "").trim() || undefined,
        create_missing_baids: effectiveAllowCreate(),
        preview_limit: safeLim,
        crosswalk_file: isClientLOS ? crosswalkFile : undefined,
        county: (baidCounty || "").trim() || undefined,
      });
      setPreviewData(res?.data || null);

      // Auto-load template for this identity
      setMappingDraft(loadMappingTemplate(currentMapKey));
    } catch (e) {
      setPreviewErr(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally {
      setPreviewBusy(false);
    }
  }

  async function uploadBAIDExcel({ column_mapping } = {}) {
    const v = validateBAIDInputs();
    if (v) {
      setErr(v);
      return;
    }

    const client = (baidClient || "").trim();

    setBusy(true);
    setErr("");
    setMsg("Running BAID import…");

    try {
      const res = await OGDB2.uploadImportExcel({
        file: baidFile,
        client,
        file_type: baidFileType,
        tax_year: baidTaxYear ? baidTaxYear : undefined,
        appraiser_firm: (isGasSearch || isClientLOS) ? undefined : (baidFirm || "").trim() || undefined,
        notes: (baidNotes || "").trim() || undefined,
        create_missing_baids: effectiveAllowCreate(),

        // ✅ send mapping if provided
        column_mapping: Array.isArray(column_mapping) ? column_mapping : undefined,
        crosswalk_file: isClientLOS ? crosswalkFile : undefined,
        county: (baidCounty || "").trim() || undefined,
      });

      const s = res?.data;
      const warn = Array.isArray(s?.errors) && s.errors.length ? ` (${s.errors.length} warnings)` : "";

      setLastImportFileId(s?.import_file_id ?? null);

      setMsg(
        `BAID import complete. Rows: ${s.created_rows}, Links: ${s.created_links}, ` +
          `Unmatched: ${s.unmatched_rows}, Units created: ${s.created_units}, ` +
          `units reused: ${s.reused_units}. create_missing_baids=${s.create_missing_baids}.${warn}`
      );

      if (Array.isArray(s?.errors) && s.errors.length) {
        console.log("BAID import warnings/errors:", s.errors);
      }

      await refreshBAIDUnits(client);
      onRefresh?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "BAID import failed");
    } finally {
      setBusy(false);
    }
  }

  async function confirmFromPreview() {
    setPreviewOpen(false);
    setPreviewErr("");

    // LOS and GasSearch handle metrics automatically — skip the column mapper
    if (isClientLOS || isGasSearch) {
      await uploadBAIDExcel();
      return;
    }

    // For other types, open mapper so user can assign columns
    const tmpl = loadMappingTemplate(currentMapKey);
    setMappingDraft(tmpl);
    setMapperOpen(true);
  }

  async function openUnitDetail(unitId) {
    setDetailOpen(true);
    setDetailBusy(true);
    setDetailErr("");
    setDetailUnit(null);
    setDetailRows([]);

    try {
      const res = await OGDB2.getBAIDUnitDetail(unitId);
      const data = res?.data;
      setDetailUnit(data?.unit || null);
      setDetailRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      setDetailErr(e?.response?.data?.detail || e?.message || "Failed to load BAID detail");
    } finally {
      setDetailBusy(false);
    }
  }

  const firmControlled = baidFileType === "lease_summary" || baidFileType === "appraisal_roll";
  const isGasSearch = String(baidFileType || "").trim().toLowerCase() === "gassearch";
  const isClientLOS = String(baidFileType || "").trim().toLowerCase() === "client_los";
  const baidFileAccept = isGasSearch
    ? ".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
    : ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4">
      <BusyOverlay
        open={busy}
        title="Import running…"
        subtitle="This can take a bit when writing lots of facts (every column) to Postgres. Don’t close the app."
      />

      <BAIDImportPreviewModal
        open={previewOpen}
        busy={previewBusy}
        error={previewErr}
        preview={previewData}
        onClose={() => {
          if (previewBusy) return;
          setPreviewOpen(false);
        }}
        onConfirm={confirmFromPreview}
      />

      <ColumnMapperModal
        open={mapperOpen}
        preview={previewData}
        client={(baidClient || "").trim()}
        fileType={baidFileType}
        firm={(baidFirm || "").trim()}
        initialMapping={mappingDraft}
        onClose={() => setMapperOpen(false)}
        onSaveAndContinue={async (payload) => {
          // Save template locally for next time
          saveMappingTemplate(currentMapKey, payload);
          setMappingDraft(payload);
          setMapperOpen(false);

          // run import with mapping
          await uploadBAIDExcel({ column_mapping: payload });
        }}
      />

      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-gray-800">Import / Export</div>
      </div>

      {err && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 mb-3">
          {String(err)}
        </div>
      )}

      {msg && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-700 mb-3 flex flex-col gap-2">
          <div>{String(msg)}</div>

          {lastImportFileId ? (
            <div className="flex flex-col md:flex-row md:items-center gap-2 text-xs text-green-800">
              <div>
                <span className="font-semibold">Import File ID:</span>{" "}
                <span className="font-mono">{lastImportFileId}</span>
              </div>

              <div className="flex gap-2">
                <button className="btn" disabled={busy} onClick={downloadLastDebugXlsx}>
                  Download Debug XLSX
                </button>
              </div>

              <div className="text-[11px] text-green-800">
                (Exports raw→normalized→fingerprint→BAID; unmatched rows included)
              </div>
            </div>
          ) : null}
        </div>
      )}

      <div className="grid gap-4">
        {/* BAID / Multi-source Excel Import */}
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
          <div className="text-xs font-semibold text-blue-900 mb-2">
            BAID Import (Seed with Lease Summary; others attach)
          </div>
          <div className="text-xs text-blue-800 mb-3">
            Lease Summary is the BAID seed (creates units). Other imports attach to existing BAIDs by default and will
            report unmatched rows.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-xs text-gray-700">
              Client <span className="text-red-600">*</span>
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                value={baidClient}
                onChange={(e) => setBaidClient(e.target.value)}
                placeholder="e.g. Eagle Ridge"
                disabled={busy}
              />
            </label>

            <label className="text-xs text-gray-700">
              File Type <span className="text-red-600">*</span>
              <select
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                value={baidFileType}
                onChange={(e) => setBaidFileType(e.target.value)}
                disabled={busy}
              >
                <option value="lease_summary">Lease Summary (SEED)</option>
                <option value="appraisal_roll">Appraisal Roll (ATTACH)</option>
                <option value="client_los">Client LOS / Financials (ATTACH)</option>
				<option value="gassearch">GasSearch (ATTACH)</option>
                <option value="other">Other</option>
              </select>
            </label>

            <label className="text-xs text-gray-700">
              Tax Year
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                value={baidTaxYear}
                onChange={(e) => setBaidTaxYear(e.target.value)}
                placeholder="e.g. 2025"
                disabled={busy}
              />
            </label>

            {!(isGasSearch || isClientLOS) && (
			  <label className="text-xs text-gray-700">
				Appraiser Firm
				{firmControlled ? (
				  <select
					className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
					value={baidFirm}
					onChange={(e) => setBaidFirm(e.target.value)}
					disabled={busy}
				  >
					{APPRAISER_FIRMS.map((f) => (
					  <option key={f.value} value={f.value}>
						{f.label}
					  </option>
					))}
				  </select>
				) : (
				  <input
					className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
					value={baidFirm}
					onChange={(e) => setBaidFirm(e.target.value)}
					placeholder="e.g. capitol"
					disabled={busy}
				  />
				)}
				{firmControlled ? (
				  <div className="text-[11px] text-gray-500 mt-1">
					Used to auto-load the default mapping template for this appraiser.
				  </div>
				) : null}
			  </label>
			)}

            {firmControlled && (
              <label className="text-xs text-gray-700 md:col-span-2">
                County <span className="text-red-600">*</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                  value={baidCounty}
                  onChange={(e) => setBaidCounty(e.target.value)}
                  placeholder="e.g. Kaufman"
                  disabled={busy}
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  The single county this file covers. Not case-sensitive. Required so
                  BAIDs with the same RRC in different counties stay distinct.
                </div>
              </label>
            )}

            <label className="text-xs text-gray-700 md:col-span-2">
              Notes
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                value={baidNotes}
                onChange={(e) => setBaidNotes(e.target.value)}
                placeholder="optional notes about this import"
                disabled={busy}
              />
            </label>

            <label className="text-xs text-gray-700 md:col-span-2">
              Preview rows (1–5000)
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white p-2 text-sm"
                value={String(previewLimit)}
                onChange={(e) => setPreviewLimit(e.target.value)}
                placeholder="500"
                disabled={busy}
              />
              <div className="text-[11px] text-gray-500 mt-1">
                Higher numbers show more rows but can slow the browser. Columns always show everything.
              </div>
            </label>
          </div>

          {baidFileType !== "lease_summary" && (
            <div className="mt-3 text-xs text-blue-900 flex items-center gap-2">
              <input
                type="checkbox"
                checked={createMissingBaids}
                onChange={(e) => setCreateMissingBaids(e.target.checked)}
                disabled={busy}
              />
              <span className="font-semibold">Allow creating BAIDs for unmatched rows</span>
              <span className="text-blue-800">(default off; recommended off)</span>
            </div>
          )}

          <div className="mt-3">
            <label className="text-xs text-gray-700">
              {isClientLOS ? "LOS / Expense file" : "Import file"} <span className="text-red-600">*</span>
              <input
                type="file"
                accept={baidFileAccept}
                disabled={busy}
                className="mt-1 block w-full text-sm"
                onChange={(e) => setBaidFile(e.target.files?.[0] || null)}
              />
              <div className="text-[11px] text-gray-500 mt-1">
                {isGasSearch
                  ? "GasSearch supports .csv or .xlsx."
                  : isClientLOS
                  ? "Client LOS Excel with flat detail sheet (Cost Center ID + Gross Amount columns)."
                  : "Excel only (.xlsx/.xls)."}
              </div>
            </label>
          </div>

          {isClientLOS && (
            <div className="mt-3">
              <label className="text-xs text-gray-700">
                Crosswalk file (BOLO → RRC) <span className="text-gray-400 font-normal">optional</span>
                <input
                  type="file"
                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  disabled={busy}
                  className="mt-1 block w-full text-sm"
                  onChange={(e) => setCrosswalkFile(e.target.files?.[0] || null)}
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  Optional fallback. Primary matching uses well names against existing BAIDs.
                </div>
              </label>
            </div>
          )}

          <div className="mt-4 flex flex-col md:flex-row gap-2">
            <button className="btn" disabled={busy} onClick={runPreview}>
              Preview Import (no write)
            </button>

            <button
              className="btn"
              disabled={busy}
              onClick={() => {
                if (isClientLOS || isGasSearch) {
                  // These importers handle metrics automatically — no mapping needed
                  uploadBAIDExcel();
                } else {
                  const tmpl = loadMappingTemplate(currentMapKey);
                  uploadBAIDExcel({ column_mapping: tmpl });
                }
              }}
              title={isClientLOS || isGasSearch
                ? "Imports immediately (metrics mapped automatically)"
                : "Imports immediately using the saved template (if any)"}
            >
              Import Now (write)
            </button>

            <button
              className="btn"
              disabled={busy}
              onClick={() => {
                const c = (baidClient || "").trim();
                if (!c) return;
                refreshBAIDUnits(c);
              }}
            >
              Refresh BAIDs
            </button>
          </div>

          <div className="mt-4">
            <div className="text-xs font-semibold text-blue-900 mb-2">
              BAID Units (client) {baidUnits?.length ? `(${baidUnits.length})` : ""}
            </div>

            <div className="overflow-auto rounded-xl border border-blue-200 bg-white">
              <table className="min-w-[900px] w-full text-xs">
                <thead className="bg-blue-50 text-blue-900 text-left">
                  <tr>
                    <th className="py-2 px-3">BAID</th>
                    <th className="py-2 px-3">Display</th>
                    <th className="py-2 px-3">Fingerprint</th>
                    <th className="py-2 px-3">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(baidUnits) ? baidUnits : []).map((u) => (
                    <tr key={u.id} className="border-t">
                      <td className="py-2 px-3 font-mono">{u.id}</td>
                      <td className="py-2 px-3">{u.display_name || ""}</td>
                      <td className="py-2 px-3 font-mono">{u.unit_fingerprint || ""}</td>
                      <td className="py-2 px-3">
                        <button className="btn" onClick={() => openUnitDetail(u.id)} disabled={detailBusy}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!baidUnits?.length ? (
                    <tr>
                      <td className="py-4 px-3 text-blue-900/70" colSpan={4}>
                        No BAIDs loaded yet. Enter client and click “Refresh BAIDs”.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail modal (unchanged) */}
          {detailOpen ? (
            <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 p-3">
              <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">BAID Detail</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Shows staged import rows currently linked to this BAID.
                    </div>
                  </div>

                  <button className="btn" onClick={() => setDetailOpen(false)} disabled={detailBusy}>
                    Close
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  {detailErr ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {String(detailErr)}
                    </div>
                  ) : null}

                  {detailBusy ? (
                    <div className="text-sm text-gray-600">Loading…</div>
                  ) : (
                    <>
                      <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs text-gray-700">
                        <div className="font-semibold">Unit</div>
                        <div className="mt-1">
                          <span className="font-mono">{detailUnit?.id}</span>{" "}
                          {detailUnit?.display_name ? `— ${detailUnit.display_name}` : ""}
                        </div>
                        <div className="mt-1 font-mono text-[11px] text-gray-600">
                          {detailUnit?.unit_fingerprint || ""}
                        </div>
                      </div>

                      <div className="overflow-auto rounded-xl border border-gray-200">
                        <table className="min-w-[1200px] w-full text-xs">
                          <thead className="bg-gray-50 text-gray-600 text-left">
                            <tr>
                              <th className="py-2 px-3">Row</th>
                              <th className="py-2 px-3">RRC</th>
                              <th className="py-2 px-3">Lease</th>
                              <th className="py-2 px-3">Operator</th>
                              <th className="py-2 px-3">County</th>
                              <th className="py-2 px-3">WI%</th>
                              <th className="py-2 px-3">RI%</th>
                              <th className="py-2 px-3">External</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(detailRows) ? detailRows : []).map((r) => (
                              <tr key={r.id} className="border-t">
                                <td className="py-2 px-3 font-mono">{r.row_index}</td>
                                <td className="py-2 px-3">{r.rrc_number || "—"}</td>
                                <td className="py-2 px-3">{r.lease_name || "—"}</td>
                                <td className="py-2 px-3">{r.operator || "—"}</td>
                                <td className="py-2 px-3">{r.county || "—"}</td>
                                <td className="py-2 px-3">{r.working_interest_pct ?? "—"}</td>
                                <td className="py-2 px-3">{r.royalty_interest_pct ?? "—"}</td>
                                <td className="py-2 px-3">{r.external_account_id || "—"}</td>
                              </tr>
                            ))}

                            {!detailRows?.length ? (
                              <tr>
                                <td className="py-4 px-3 text-gray-500" colSpan={8}>
                                  No rows linked.
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
